// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * main.ts — boot and the screen machine. Owns no game rules.
 *
 * The structural rule this file enforces is ONE ROOM PER SESSION: a `Net` is
 * created when the player enters a room and lives until they walk back to the
 * menu; every rematch happens INSIDE it via rematch.ts. Leaving and re-joining a
 * room to "reset" hands back a Trystero room mid-teardown, the mesh never forms,
 * and both players sit alone in the correct room code forever.
 */

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type Rounds, type RoundInfo } from '@ben-gy/game-engine/rematch';
import {
  clearRoomInUrl,
  createLobby,
  createRoomEntry,
  normalizeRoomCode,
  setRoomInUrl,
} from '@ben-gy/game-engine/lobby';
import { newSeed } from '@ben-gy/game-engine/rng';

import { Game, dailySeed, type Summary } from './game';
import { DEFAULT_MODE, MODES, modeOf, type Mode } from './modes';
import { createRace, resultOf, type Race, type Standing } from './race';
import { createPlay, type PlayView } from './play';
import { opponentField } from './bot';
import {
  mountShell,
  renderAbout,
  renderHelp,
  renderMenu,
  renderResults,
  type ResultEntry,
} from './ui';

const SLUG = 'boxbox';
const MIN_PLAYERS = 2;
const MAX_PLAYERS = 6;

const store = createStore(SLUG);
const sfx = createSfx(store.get('muted', false));

const root = document.getElementById('app');
if (!root) throw new Error('missing #app');
const shell = mountShell(root);

let mode: Mode = modeOf(store.get<string>('mode', DEFAULT_MODE.id));

// ── live session state ────────────────────────────────────────────────────────

let game: Game | null = null;
let play: PlayView | null = null;
let race: Race | null = null;
let resultsTimer: ReturnType<typeof setInterval> | undefined;

let net: Net | null = null;
let rounds: Rounds | null = null;
let roomCode = '';
let lastSummary: Summary | null = null;
let curRound = 0;

/** Wins per player id, across the life of the room (the running match tally). */
const wins = new Map<string, number>();
const nameById = new Map<string, string>();
const tallied = new Set<number>();

const bestKey = (m: Mode): string => `best:${m.id}`;

function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
}

function teardownRound(): void {
  play?.destroy();
  play = null;
  race?.destroy();
  race = null;
  game = null;
  if (resultsTimer) clearInterval(resultsTimer);
  resultsTimer = undefined;
}

async function leaveRoom(): Promise<void> {
  rounds?.destroy();
  rounds = null;
  const n = net;
  net = null;
  roomCode = '';
  wins.clear();
  nameById.clear();
  tallied.clear();
  clearRoomInUrl();
  if (n) await n.leave();
}

// ── screens ───────────────────────────────────────────────────────────────────

function showMenu(): void {
  teardownRound();
  setPlaying(false);
  lastSummary = null;
  void leaveRoom();
  renderMenu(shell.view, {
    mode,
    best: store.get(bestKey(mode), 0),
    muted: sfx.muted(),
    onMode: (m) => {
      mode = m;
      store.set('mode', m.id);
      showMenu();
    },
    onSolo: () => startSolo(newSeed()),
    onDaily: () => startSolo(dailySeed()),
    onFriends: () => showRoomEntry(),
    onHelp: () => renderHelp(shell.view, showMenu),
    onAbout: () => renderAbout(shell.view, showMenu),
    onMute: (m) => {
      sfx.setMuted(m);
      store.set('muted', m);
      showMenu();
    },
  });
}

function showRoomEntry(): void {
  teardownRound();
  setPlaying(false);
  shell.view.innerHTML = '';
  createRoomEntry({
    container: shell.view,
    title: 'Play with friends',
    subtitle: 'Everyone races the identical seed. Start a room, or type a friend’s code.',
    onSubmit: (code, created) => void enterRoom(code, created),
    onCancel: showMenu,
  });
}

// ── solo ───────────────────────────────────────────────────────────────────────

function startSolo(seed: number): void {
  teardownRound();
  sfx.unlock();
  beginRound(seed, mode, false, 0);
}

// ── multiplayer ─────────────────────────────────────────────────────────────────

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardownRound();
  sfx.unlock();
  await leaveRoom();

  roomCode = normalizeRoomCode(code);
  setRoomInUrl(roomCode);

  net = createNet(
    { appId: roomAppId(SLUG), roomId: roomCode, claimHost: created },
    {
      onHostChange: (_id, isSelf) => {
        race?.setHost(isSelf);
        repaintLobbyish();
      },
      onPeerLeave: (id) => {
        race?.peerLeft(id);
        repaintLobbyish();
      },
      onPeers: () => repaintLobbyish(),
    },
  );

  rounds = createRounds({
    net,
    playerName: myName(),
    minPlayers: MIN_PLAYERS,
    roundOpts: () => ({ mode: mode.id }),
    onRound: (info) => onRoundStart(info),
    onChange: () => repaintLobbyish(),
  });

  showLobby();
}

function myName(): string {
  const existing = store.get<string>('name', '');
  if (existing) return existing;
  const name = `Crew ${1 + Math.floor(Math.random() * 99)}`;
  store.set('name', name);
  return name;
}

function showLobby(): void {
  if (!net || !rounds) return showMenu();
  teardownRound();
  setPlaying(false);
  shell.view.innerHTML = '';
  createLobby({
    container: shell.view,
    net,
    rounds,
    roomCode,
    minPlayers: MIN_PLAYERS,
    maxPlayers: MAX_PLAYERS,
    onCancel: showMenu,
  });
}

function repaintLobbyish(): void {
  if (!rounds) return;
  const state = rounds.state();
  if (state.phase === 'playing' && game && !game.over) return;
  if (lastSummary) return showResults();
  if (!play) showLobby();
}

function onRoundStart(info: RoundInfo): void {
  lastSummary = null;
  if (!info.seated) {
    showLobby();
    return;
  }
  const opts = (info.opts ?? {}) as { mode?: string };
  const roundMode = modeOf(opts.mode);
  for (const p of info.players) nameById.set(p.id, p.name);
  beginRound(info.seed, roundMode, true, info.round, info);
}

// ── a round ─────────────────────────────────────────────────────────────────────

function beginRound(
  seed: number,
  roundMode: Mode,
  multiplayer: boolean,
  round: number,
  info?: RoundInfo,
): void {
  teardownRound();
  curRound = round;

  const g = new Game({ seed, mode: roundMode });
  game = g;

  if (multiplayer && net && info) {
    race = createRace({
      net,
      round,
      seats: info.players.map((p) => ({ id: p.id, name: p.name })),
      selfId: net.selfId,
      isHost: net.isHost(),
      onChange: () => {
        play?.setStandings(race?.standings() ?? []);
        if (lastSummary) showResults();
      },
      onLadder: () => showResults(),
    });
  }

  const view = createPlay({
    game: g,
    mode: roundMode,
    multiplayer,
    sfx,
    onProgress: (stopIndex, total) => race?.ping(stopIndex, g.stops, total),
    onFinish: (summary) => finishRun(summary),
    onMenu: showMenu,
  });
  play = view;

  shell.view.innerHTML = '';
  shell.view.appendChild(view.root);
  view.setStandings(race?.standings() ?? []);
  setPlaying(true);
}

function finishRun(summary: Summary): void {
  lastSummary = summary;
  sfx.play(summary.catastrophes > 0 ? 'lose' : 'win');

  const key = bestKey(game!.mode);
  const prev = store.get(key, 0);
  if (!prev || summary.total < prev) store.set(key, summary.total);

  if (race) {
    race.finish(resultOf(summary));
    showResults();
    return;
  }
  showResults();
}

// ── results ─────────────────────────────────────────────────────────────────────

function soloEntries(g: Game, summary: Summary): ResultEntry[] {
  const field = opponentField(g.seed, g.mode, ['Ada', 'Bex', 'Cal', 'Dane', 'Ez']);
  const entries: ResultEntry[] = field.map((f) => ({
    name: f.name,
    isSelf: false,
    total: f.total,
    catastrophes: f.catastrophes,
    done: true,
    gone: false,
  }));
  entries.push({
    name: 'You',
    isSelf: true,
    total: summary.total,
    catastrophes: summary.catastrophes,
    done: true,
    gone: false,
  });
  entries.sort((a, b) => a.total - b.total || a.catastrophes - b.catastrophes);
  return entries;
}

function raceEntries(list: Standing[]): ResultEntry[] {
  return list.map((s) => ({
    name: s.name,
    isSelf: s.isSelf,
    total: s.total,
    catastrophes: s.result?.catastrophes ?? 0,
    done: s.done,
    gone: s.gone,
  }));
}

function tallyIfSettled(): void {
  if (!race || !race.settled() || tallied.has(curRound)) return;
  tallied.add(curRound);
  const done = race.standings().filter((s) => s.done && !s.gone);
  if (done.length === 0) return;
  const winner = done.reduce((a, b) => (b.total < a.total ? b : a));
  wins.set(winner.id, (wins.get(winner.id) ?? 0) + 1);
}

function tallyText(): string {
  if (wins.size === 0) return '';
  const parts = [...wins.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([id, n]) => `${nameById.get(id) ?? '—'} ${n}`);
  return `Match: ${parts.join(' · ')}`;
}

function showResults(): void {
  const summary = lastSummary;
  const g = game;
  if (!summary || !g) return;

  setPlaying(false);
  tallyIfSettled();

  const multiplayer = !!race;
  const state = rounds?.state();
  const votes = state
    ? state.votes.length
      ? `Ready: ${state.votes.map((v) => v.name).join(', ')} (${state.votes.length}/${state.present.length})`
      : 'Nobody has hit play again yet.'
    : '';

  const entries = multiplayer ? raceEntries(race!.standings()) : soloEntries(g, summary);

  renderResults(shell.view, {
    mode: g.mode,
    summary,
    best: store.get(bestKey(g.mode), 0),
    entries,
    multiplayer,
    closesInMs: race?.closesInMs() ?? null,
    votes,
    startsInMs: state?.startsInMs ?? null,
    canForceStart: !!state?.isHost && !!state?.canStart,
    tally: tallyText(),
    onAgain: () => {
      if (rounds) {
        rounds.finish();
        rounds.vote();
        showResults();
      } else {
        startSolo(newSeed());
      }
    },
    onLobby: () => {
      lastSummary = null;
      rounds?.finish();
      showLobby();
    },
    onMenu: showMenu,
    onShare: () => void shareRun(g, summary),
    onForceStart: () => rounds?.go(),
    onFeedback: (from) => (window as any).feedback?.open({ returnFocusTo: from }),
  });

  if (!resultsTimer && multiplayer) {
    resultsTimer = setInterval(() => {
      if (lastSummary) showResults();
      else if (resultsTimer) {
        clearInterval(resultsTimer);
        resultsTimer = undefined;
      }
    }, 500);
  }
}

async function shareRun(g: Game, summary: Summary): Promise<void> {
  const url = new URL(location.href);
  url.search = '';
  url.searchParams.set('seed', String(g.seed));
  url.searchParams.set('mode', g.mode.id);
  const text = `I ran ${summary.total.toFixed(1)}s on this Boxbox race (${g.mode.name}). Same seed — beat it.`;
  try {
    if (navigator.share) {
      await navigator.share({ title: 'Boxbox', text, url: url.toString() });
      return;
    }
    await navigator.clipboard.writeText(`${text} ${url.toString()}`);
    // A tiny confirmation without a toast host on this screen.
    window.prompt('Link copied — share it:', `${text} ${url.toString()}`);
  } catch {
    window.prompt('Copy this link:', `${text} ${url.toString()}`);
  }
}

// ── boot ────────────────────────────────────────────────────────────────────────

async function boot(): Promise<void> {
  hardenViewport();

  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    // Fails open to STUN-only; TURN is an upgrade, never a dependency.
  }

  const params = new URLSearchParams(location.search);
  const room = params.get('room');
  const seed = params.get('seed');
  const linkMode = params.get('mode');

  if (room) {
    await enterRoom(room, false);
    return;
  }
  if (seed) {
    const n = Number(seed);
    if (Number.isFinite(n)) {
      mode = modeOf(linkMode);
      startSolo(n >>> 0);
      return;
    }
  }
  showMenu();
}

window.addEventListener('beforeunload', () => {
  void net?.leave();
});

const unlock = (): void => sfx.unlock();
window.addEventListener('pointerdown', unlock, { once: true });
window.addEventListener('keydown', unlock, { once: true });

void boot();

export { MODES };
export type { Standing };
